import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import Container from '@mui/material/Container';
import MenuItem from '@mui/material/MenuItem';
import CardMedia from '@mui/material/CardMedia';
import Divider from '@mui/material/Divider';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';

import { useLogoutMutation } from '#services/auth';
import LanguageSwitcher from '#components/LanguageSwitcher';

interface HeaderUserInfo {
  username?: string;
  role?: string;
  code?: string | number;
  isCustomer?: boolean;
}

interface NavItem {
  name: string;
  value: string;
}

function Header() {
  const isLoggedIn = useSelector((state: { auth: { isLoggedIn: boolean } }) => state.auth.isLoggedIn);
  const userInfo = useSelector((state: { auth: { userInfo: HeaderUserInfo } }) => state.auth.userInfo);
  const cartItemCount = useSelector((state: { misc: { cartItemCount?: number } }) => state.misc.cartItemCount);
  const appConfig = useSelector(
    (state: { appConfig: { wholesaleLogo?: string; wholesaleName?: string } }) => state.appConfig,
  );

  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [doLogout] = useLogoutMutation();
  const matchMobileWidth = useMediaQuery('(max-width:600px)', { noSsr: true });

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const isManagementPage = location.pathname.startsWith('/management');
  const showCartIcon = !isManagementPage;

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleGotoOrders = () => {
    setAnchorEl(null);
    void navigate('/order');
  };

  const handleSignOut = () => {
    setAnchorEl(null);
    void doLogout(undefined).then(() => {
      void navigate('/');
    });
  };

  const isInventory = location.search.includes('inventory=true');
  const useMobileHeader = !isManagementPage && matchMobileWidth;
  const cartUrl = isInventory
    ? '/cart?inventory=true'
    : '/cart';

  const renderProfileMenu = () => {
    if (!isLoggedIn) {
      return null;
    }

    const initial = (userInfo.username || '?').trim().charAt(0).toUpperCase();
    const tooltipTitle = `${userInfo.username} (${userInfo.role}: ${userInfo.code})`;

    return (
      <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {
          showCartIcon && (
            <IconButton size="large" color="inherit" onClick={() => { void navigate(cartUrl); }}>
              <Badge badgeContent={cartItemCount} color="error">
                <ShoppingCartIcon />
              </Badge>
            </IconButton>
          )
        }
        <Tooltip title={tooltipTitle}>
          <IconButton
            id="profile-button"
            aria-controls={open ? 'profile-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClick}
            sx={{ p: 0.5 }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: '#d9a258',
                color: '#fff',
                fontSize: '0.82rem',
                fontWeight: 500,
              }}
            >
              {initial}
            </Avatar>
          </IconButton>
        </Tooltip>
        <Menu
          id="profile-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          slotProps={{ list: { 'aria-labelledby': 'profile-button' } }}
        >
          {userInfo.isCustomer && <MenuItem onClick={handleGotoOrders}>{t('profileMenu.myOrders')}</MenuItem>}
          <MenuItem onClick={handleSignOut}>{t('profileMenu.signOut')}</MenuItem>
        </Menu>

      </Box>
    );
  };

  const renderSubMenu = (menuItems: NavItem[]) => {
    const handleChangeSubMenu = (e: React.SyntheticEvent, value: string) => {
      e.preventDefault();
      void navigate(value);
    };

    const selectedItem = [...menuItems]
      .sort((a, b) => b.value.length - a.value.length)
      .find((mi) => location.pathname.startsWith(mi.value));
    const selectedValue = selectedItem
      ? selectedItem.value
      : false;

    return (
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Tabs value={selectedValue} onChange={handleChangeSubMenu}>
          {
            menuItems.map((mi) => (
              <Tab
                key={mi.value}
                label={mi.name}
                value={mi.value}
                href={mi.value}
              />
            ))
          }
        </Tabs>
      </Box>
    );
  };

  const renderAdminSubMenu = () => {
    if (userInfo.isCustomer) {
      return null;
    }

    if (!['/shop/add', '/cart'].includes(location.pathname)) {
      return null;
    }

    const urlParam = new URLSearchParams(location.search);
    const menuItems = [{
      name: t('nav.shopAll'),
      value: `/shop${urlParam.get('inventory') === 'true' ? '?inventory=true' : ''}`,
    }];

    return renderSubMenu(menuItems);
  };

  const CUSTOMER_NAV_ITEMS: NavItem[] = [{
    name: t('nav.home'),
    value: '/',
  }, {
    name: t('nav.shopAll'),
    value: '/shop',
  }, {
    name: t('nav.myOrders'),
    value: '/order',
  }, {
    name: t('nav.myInvoices'),
    value: '/invoices',
  }, {
    name: t('nav.inventoryInquiry'),
    value: '/inventory/report',
  }];

  const renderCustomerNav = () => {
    if (!userInfo.isCustomer) {
      return null;
    }

    const selectedItem = [...CUSTOMER_NAV_ITEMS]
      .sort((a, b) => b.value.length - a.value.length)
      .find((mi) => (mi.value === '/' ? location.pathname === '/' : location.pathname.startsWith(mi.value)));
    const selectedValue = selectedItem ? selectedItem.value : false;

    const handleChange = (e: React.SyntheticEvent, value: string) => {
      e.preventDefault();
      void navigate(value);
    };

    return (
      <Tabs
        value={selectedValue}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 40,
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTab-root': {
            minHeight: 40,
            minWidth: 0,
            px: 1.75,
            py: 1,
            mx: 0.25,
            borderRadius: 1,
            fontSize: '0.875rem',
            fontWeight: 500,
            letterSpacing: '0.02857em',
            color: 'text.primary',
            '&:hover': { bgcolor: 'action.hover' },
            '&.Mui-selected': { color: 'primary.main' },
          },
        }}
      >
        {CUSTOMER_NAV_ITEMS.map((mi) => (
          <Tab
            key={mi.value}
            label={mi.name}
            value={mi.value}
            onClick={(e) => {
              e.preventDefault();
              void navigate(mi.value);
            }}
          />
        ))}
      </Tabs>
    );
  };

  if (!userInfo) {
    return null;
  }

  const imgUrl = appConfig.wholesaleLogo
    ? `/public/static/images/${appConfig.wholesaleLogo}`
    : '/public/static/images/default-logo.png';

  return (
    <Container maxWidth="xl" sx={{ displayPrint: 'none' }}>
      <Toolbar
        disableGutters
        sx={{
          gap: useMobileHeader ? 1 : 2,
          flexWrap: useMobileHeader ? 'wrap' : 'nowrap',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h5"
          noWrap
          component="a"
          href="#"
          onClick={() => { void navigate('/'); }}
          sx={{
            mr: useMobileHeader ? 0 : 1,
            display: 'flex',
            flexShrink: 0,
            flexGrow: useMobileHeader ? 1 : 0,
            minWidth: 0,
            fontFamily: 'monospace',
            fontWeight: 700,
            letterSpacing: '.3rem',
            color: 'inherit',
            textDecoration: 'none',
          }}
        >
          <CardMedia
            sx={{
              height: useMobileHeader ? 54 : 94,
              width: useMobileHeader ? 'min(220px, 58vw)' : 360,
              backgroundSize: 'contain',
            }}
            image={imgUrl}
            title={appConfig.wholesaleName}
          />
        </Typography>

        <Box sx={{ flexGrow: 1, display: useMobileHeader ? 'none' : 'block' }} />

        {!useMobileHeader && userInfo.isCustomer && renderCustomerNav()}

        <LanguageSwitcher />

        {renderProfileMenu()}
      </Toolbar>

      {useMobileHeader && userInfo.isCustomer && (
        <Box sx={{ width: '100%', overflow: 'hidden' }}>
          {renderCustomerNav()}
        </Box>
      )}

      <Divider />
      {renderAdminSubMenu()}
    </Container>
  );
}

export default Header;
